/** Окно уходимости карточки и пороги партии ДЛЯ ПОДПИСИ.
 *
 * САМ РАСЧЁТ «К ЗАКАЗУ» ЖИВЁТ НА СЕРВЕРЕ (`app/core/modules/plan/reorder.py`) и
 * приезжает готовым в ответе `/api/product/{id}` — по каждому размеру и сразу
 * по четырём окнам, поэтому переключатель окна в базу не ходит. Спека —
 * `docs/specs/2026-09-16-reorder-per-platform-design.md`.
 *
 * ДО 16.09.26 ФОРМУЛА СЧИТАЛАСЬ ЗДЕСЬ, а на сервере стояла её копия для кольца
 * листания, и сходимость держал тест, вычитывавший этот файл грепом. Формула
 * стала считать нехватку КАЖДОЙ площадки отдельно (запас Ozon не закрывает
 * дефицит WB — товар оттуда не забрать), входов стало восемь, и сверка текстом
 * перестала быть защитой: она ловила бы расхождение букв, а не смысла.
 *
 * MOQ и MIN_GAP остаются ЧИСЛАМИ ПОДПИСИ — их называет подсказка строки, а
 * считает ими сервер; расхождение ловит `tests/test_plan_reorder.py`. */

/** Меньше пяти штук размера фабрика не шьёт. */
export const MOQ = 5;
/** Нехватку меньше трёх штук партией не лечат — это шум уходимости. */
export const MIN_GAP = 3;

/** Окна списка уходимости карточки. Правило разбора `w` ОДНО на
 * карточку и шестерёнку листания: разойдись дефолт, кольцо сортировало бы по
 * одному окну, а таблица открытой карточки показывала бы другое.
 *
 * Дефолт 60 — единственное окно, где число есть у 88 % размеров (замер
 * боевой CRM 02.09.26); на семи днях у трёх четвертей движения нет вовсе.
 * 90 дней — с 15.09.26; список обязан совпадать с `WINDOWS` сервера, иначе
 * кольцо ответит 422 `WINDOW_UNKNOWN`. */
export const SPEED_WINDOWS = [7, 30, 60, 90] as const;
const DEFAULT_SPEED_WINDOW = 60;

export function speedWindow(params: URLSearchParams): number {
  const w = Number(params.get("w"));
  return (SPEED_WINDOWS as readonly number[]).includes(w) ? w : DEFAULT_SPEED_WINDOW;
}

/**
 * Окно уходимости раздела «Товары» (разметка 26.09.26 06:36 п.2): пресет числом
 * или СВОЙ ПЕРИОД строкой `2025-10-01:2025-12-31` — например, прошлый сезон.
 * Формула «к заказу» та же, меняется только отрезок. Свой период понимают
 * кольцо (`/api/demand/ranking`, `/api/supplier/{id}`, `/api/models`) и список
 * (`/api/products/sizes`); остальные экраны читают `speedWindow` — период там
 * становится дефолтом, как любой чужой `w`.
 */
export type WindowValue = number | string;

const SPAN = /^(\d{4}-\d{2}-\d{2}):(\d{4}-\d{2}-\d{2})$/;

/** Границы своего периода; у пресета — `null`. */
export function windowSpan(w: WindowValue): [string, string] | null {
  const got = typeof w === "string" ? SPAN.exec(w) : null;
  return got && got[1] && got[2] && got[1] <= got[2] ? [got[1], got[2]] : null;
}

/** `w` раздела «Товары»: свой период — как есть, иначе правило `speedWindow`. */
export function productsWindow(params: URLSearchParams): WindowValue {
  const raw = params.get("w") ?? "";
  return windowSpan(raw) ? raw : speedWindow(params);
}

/** Окно по датам пикера: «последние N дней по сегодня» — пресет, прочее — период. */
export function windowOf(from: string, to: string, today: string): WindowValue {
  const дней = Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000) + 1;
  return to === today && (SPEED_WINDOWS as readonly number[]).includes(дней) ? дней : `${from}:${to}`;
}

/** Дней в окне — делитель скорости. */
export function windowLength(w: WindowValue): number {
  const span = windowSpan(w);
  return span ? Math.round((Date.parse(span[1]) - Date.parse(span[0])) / 86_400_000) + 1 : Number(w);
}

/** Подпись окна в тексте: «60 дней» или «01.10.25 — 31.12.25». */
export function speedWindowLabel(w: WindowValue): string {
  const span = windowSpan(w);
  if (!span) return `${w} дней`;
  const коротко = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(2, 4)}`;
  return `${коротко(span[0])} — ${коротко(span[1])}`;
}
